import React from 'react';

import { translate } from '../../../base/i18n';
import i18next, { TRANSLATION_LANGUAGES_HEAD } from '../../../base/i18n/i18next';
import { FixedIcon, FloatingIcon, Icon, IconSettings } from '../../../base/icons';
import { DropdownItem, DropdownItemGroup, DropdownMenu } from '../../../base/react';
import { connect } from '../../../base/redux';
import { showErrorNotification } from '../../../notifications';
import LIBRE_SUPPORTED_LANGS from '../../../transcribing/libre-supported-langs.json';
import {
    closeCaptionsModal,
    selectTranslationLanguage,
    setCaptionsView,
    setRequestingSubtitles,
    setTranslatedVoiceEnabled,
    toggleLanguageSelectorDialog
} from '../../actions.web';
import { getTranslatedVoiceUnavailableReason } from '../../functions.web';
import {
    AbstractCaptions,
    type AbstractCaptionsProps as Props,
    _abstractMapStateToProps
} from '../AbstractCaptions';
import { stylesInMethos } from '../styles';

const NON_DRAGGABLE_SELECTOR = [
    'button',
    'input',
    'textarea',
    'select',
    'a',
    '.captions-dropdown-menu',
    '.captions-dropdown-items-scroll',
    '.dropdown-menu',
    '.captions-language-item',
    '.captions-fixed-icon-btn',
    '.captions-jump-to-bottom-btn',
    '.captions-fixed-language-pill',
    '.captions-voice-toggle',
    '.captions-fixed-text'
].join(', ');

/**
 * React {@code Component} which can display speech-to-text results from
 * Jigasi as subtitles.
 */
class Captions extends AbstractCaptions<Props> {
    state = {
        position: {
            x: -286,
            y: 30
        },
        isDragging: false,
        isDragged: false,
        showCurrentCaption: true,
        dragStart: {
            x: 0,
            y: 0
        },
        searchQuery: '',
        captionFontSize: 'normal'
    };

    containerRef = React.createRef();
    topScrollRef = React.createRef();
    fixedScrollRef = React.createRef();

    /**
     * Constrains the given coordinates so that the captions container
     * doesn't exceed the window's width and height.
     *
     * @param {number} newX - The candidate X translation coordinate.
     * @param {number} newY - The candidate Y translation coordinate.
     * @private
     * @returns {Object} The constrained X and Y coordinates.
     */
    _constrainPosition = (newX: number, newY: number) => {
        const container = this.containerRef.current;

        if (!container) {
            return {
                x: newX,
                y: newY
            };
        }

        const rect = container.getBoundingClientRect();
        const currentPosition = this.state.position;

        // Limit X
        const minX = currentPosition.x - rect.left;
        const maxX = currentPosition.x + (window.innerWidth - rect.right);
        const constrainedX = minX <= maxX ? Math.max(minX, Math.min(maxX, newX)) : newX;

        // Limit Y
        const minY = currentPosition.y - rect.top;
        const maxY = currentPosition.y + (window.innerHeight - rect.bottom);
        const constrainedY = minY <= maxY ? Math.max(minY, Math.min(maxY, newY)) : newY;

        return {
            x: constrainedX,
            y: constrainedY
        };
    };

    /**
     * Handles the mouse down event to initiate dragging.
     *
     * @param {MouseEvent} e - The mouse event.
     * @private
     * @returns {void}
     */
    _onHandleMouseDown = (e: MouseEvent) => {
        if (this.props._captionsView === 'fixed') {
            return;
        }

        if (e.button !== 0) {
            return;
        } // Only handle left click

        const target = e.target;

        if (target && target.closest && target.closest(NON_DRAGGABLE_SELECTOR)) {
            return;
        }

        // Get the current position from the element's computed style if not dragged yet
        const currentPosition = this.state.isDragged
            ? this.state.position
            : this._getCurrentElementPosition();

        this.setState({
            isDragging: true,
            isDragged: true,
            position: currentPosition,
            dragStart: {
                x: e.clientX - currentPosition.x,
                y: e.clientY - currentPosition.y
            }
        });

        e.preventDefault();
    };

    /**
     * Handles the touch start event for mobile devices.
     *
     * @param {TouchEvent} e - The touch event.
     * @private
     * @returns {void}
     */
    _onHandleTouchStart = (e: TouchEvent) => {
        if (this.props._captionsView === 'fixed') {
            return;
        }

        const target = e.target;

        if (target && target.closest && target.closest(NON_DRAGGABLE_SELECTOR)) {
            return;
        }

        const touch = e.touches && e.touches[0];

        if (!touch) {
            return;
        }

        // Get the current position from the element's computed style if not dragged yet
        const currentPosition = this.state.isDragged
            ? this.state.position
            : this._getCurrentElementPosition();

        this.setState({
            isDragging: true,
            isDragged: true,
            position: currentPosition,
            dragStart: {
                x: touch.clientX - currentPosition.x,
                y: touch.clientY - currentPosition.y
            }
        });
    };

    /**
     * Handles the mouse move event during dragging.
     *
     * @param {MouseEvent} e - The mouse event.
     * @private
     * @returns {void}
     */
    _handleMouseMove = (e: MouseEvent) => {
        if (!this.state.isDragging) {
            return;
        }

        // If no buttons are pressed (e.g. mouseup was missed/fired elsewhere like PiP), reset dragging
        if (e.buttons === 0) {
            this.setState({ isDragging: false });

            return;
        }

        this.setState(prevState => {
            const newX = e.clientX - prevState.dragStart.x;
            const newY = e.clientY - prevState.dragStart.y;
            const constrained = this._constrainPosition(newX, newY);

            return {
                position: constrained
            };
        });
    };

    /**
     * Handles the touch move event for mobile devices.
     *
     * @param {TouchEvent} e - The touch event.
     * @private
     * @returns {void}
     */
    _handleTouchMove = (e: TouchEvent) => {
        if (!this.state.isDragging) {
            return;
        }

        const touch = e.touches && e.touches[0];

        if (!touch) {
            return;
        }

        const newX = touch.clientX - this.state.dragStart.x;
        const newY = touch.clientY - this.state.dragStart.y;
        const constrained = this._constrainPosition(newX, newY);

        this.setState({
            position: constrained
        });

        if (e.cancelable) {
            e.preventDefault();
        }
    };

    /**
     * Handles the mouse up event to end dragging.
     *
     * @private
     * @returns {void}
     */
    _handleMouseUp = () => {
        if (this.state.isDragging) {
            this.setState({ isDragging: false });
        }
    };

    /**
     * Handles the touch end event for mobile devices.
     *
     * @private
     * @returns {void}
     */
    _handleTouchEnd = () => {
        if (this.state.isDragging) {
            this.setState({ isDragging: false });
        }
    };

    /**
     * Handles window focus and blur events to reset dragging state.
     *
     * @private
     * @returns {void}
     */
    _handleWindowBlurOrFocus = () => {
        if (this.state.isDragging) {
            this.setState({ isDragging: false });
        }
    };

    /**
     * Handles window resize events to ensure the captions container
     * stays within the bounds of the updated window dimensions.
     *
     * @private
     * @returns {void}
     */
    _handleResize = () => {
        if (window.innerWidth < 500) {
            if (this.state.isDragged) {
                this.setState({
                    isDragged: false,
                    isDragging: false
                });
                this._initializePosition();
            }

            return;
        }

        if (this.state.isDragged) {
            this.setState(prevState => {
                const constrained = this._constrainPosition(prevState.position.x, prevState.position.y);

                return {
                    position: constrained
                };
            });
        }
    };

    /**
     * Initializes the position of the container relative to screen dimensions.
     *
     * @private
     * @returns {void}
     */
    _initializePosition = () => {
        const { tileviewStatus } = this.props;
        const initialY = tileviewStatus ? 30 : 20;
        const container = this.containerRef.current;
        const width = container ? container.getBoundingClientRect().width : 572;

        this.setState({
            position: {
                x: -width / 2,
                y: initialY
            }
        });
    };

    /**
     * Get the current element position based on default transform values.
     *
     * @private
     * @returns {Object} Current position object with x and y coordinates.
     */
    _getCurrentElementPosition = () => {
        const { tileviewStatus } = this.props;
        const container = this.containerRef.current;
        const width = container ? container.getBoundingClientRect().width : 572;

        return {
            x: -width / 2,
            y: tileviewStatus ? 30 : 20
        };
    };

    /**
     * Calculates the best dropdown placement to keep it within viewport.
     *
     * @private
     * @returns {string} Placement string.
     */
    _getDropdownPlacement = () => {
        const container = this.containerRef.current;

        if (container) {
            const rect = container.getBoundingClientRect();

            if (rect.top < window.innerHeight * 0.45) {
                return 'bottom-start';
            }
        }

        return 'top-start';
    };

    /**
     * Sets up event listeners when component mounts.
     *
     * @returns {void}
     */
    componentDidMount() {
        document.addEventListener('mousemove', this._handleMouseMove);
        document.addEventListener('mouseup', this._handleMouseUp);
        document.addEventListener('touchmove', this._handleTouchMove);
        document.addEventListener('touchend', this._handleTouchEnd);
        window.addEventListener('resize', this._handleResize);

        window.addEventListener('blur', this._handleWindowBlurOrFocus);
        window.addEventListener('focus', this._handleWindowBlurOrFocus);

        this._initializePosition();
    }

    componentDidUpdate() {
        if (this.fixedScrollRef.current && this.state.showCurrentCaption) {
            this.fixedScrollRef.current.scrollTop = this.fixedScrollRef.current.scrollHeight;
        }
    }

    /**
     * Cleans up event listeners when component unmounts.
     *
     * @returns {void}
     */
    componentWillUnmount() {
        document.removeEventListener('mousemove', this._handleMouseMove);
        document.removeEventListener('mouseup', this._handleMouseUp);
        document.removeEventListener('touchmove', this._handleTouchMove);
        document.removeEventListener('touchend', this._handleTouchEnd);
        window.removeEventListener('resize', this._handleResize);
        window.removeEventListener('blur', this._handleWindowBlurOrFocus);
        window.removeEventListener('focus', this._handleWindowBlurOrFocus);
    }

    /**
     * Renders the transcription text fallback.
     *
     * @param {string} id - The ID of the transcript message.
     * @param {string} text - Subtitles text.
     * @protected
     * @returns {React$Element}
     */
    _renderParagraph(id: string, text: string): React$Element<*> {
        return (
            <div
                key = { id }
                style = { stylesInMethos.noMargin }>
                <span>{ text }</span>
            </div>
        );
    }

    // Close captions on click of close
    _onHandleCloseCaptionsModal = () => {
        const container = this.containerRef.current;

        if (this.props._captionsView === 'fixed') {
            this.props.dispatch(closeCaptionsModal(false));

            return;
        }

        const close = () => this.props.dispatch(closeCaptionsModal(false));
        const label = this.props.t('toolbar.accessibilityLabel.cc');

        // The footer CC button; the overflow menu may hold a hidden copy with the same label, so take a visible one.
        const target = [ ...document.querySelectorAll(`[aria-label="${label}"]`) ]
            .find(el => el.getBoundingClientRect().width > 0);

        if (!container || !target || !container.animate
                || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            close();

            return;
        }

        if (this._closing) {
            return;
        }
        this._closing = true;

        const from = container.getBoundingClientRect();
        const to = target.getBoundingClientRect();
        const base = getComputedStyle(container).transform;
        const start = base === 'none' ? '' : base;

        // Scaling around the button's centre makes the panel shrink straight into it.
        container.style.transformOrigin = `${to.left + (to.width / 2) - from.left}px ${
            to.top + (to.height / 2) - from.top}px`;

        container.animate([
            { filter: 'blur(0)',
                opacity: 1,
                transform: `${start} scale(1)` },
            { filter: 'blur(0)',
                offset: 0.55,
                opacity: 0.9,
                transform: `${start} scale(0.55, 0.3)` },
            { filter: 'blur(2px)',
                opacity: 0,
                transform: `${start} scale(0.02)` }
        ], {
            duration: 420,
            easing: 'cubic-bezier(0.55, 0, 0.45, 1)',
            fill: 'forwards'
        }).finished.then(() => {
            this._closing = false;
            target.classList.add('captions-landed');
            setTimeout(() => target.classList.remove('captions-landed'), 450);
            close();
        });
    };

    _onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchQuery: e.target.value });
    };

    _onFixedScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 25;

        if (this.state.showCurrentCaption !== isAtBottom) {
            this.setState({ showCurrentCaption: isAtBottom });
        }
    };

    _onJumpToBottom = () => {
        if (this.fixedScrollRef.current) {
            this.fixedScrollRef.current.scrollTo({
                top: this.fixedScrollRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
        this.setState({ showCurrentCaption: true });
    };

    _onToggleFontSize = () => {
        this.setState(prevState => {
            return {
                captionFontSize: prevState.captionFontSize === 'large' ? 'normal' : 'large'
            };
        });
    };

    _onToggleCaptionsView = () => {
        this.props.dispatch(setCaptionsView(this.props._captionsView === 'fixed' ? 'float' : 'fixed'));
    };

    _onToggleTranslatedVoice = () => {
        if (this.props._translatedVoiceUnavailableReason) {
            return;
        }
        this.props.dispatch(setTranslatedVoiceEnabled(!this.props._translatedVoiceEnabled));
    };

    /**
     * Renders the two-bubble "A / 文" translation mark shown on the language picker.
     *
     * @private
     * @returns {React$Element}
     */
    _renderTranslateIcon() {
        return (
            <svg
                aria-hidden = 'true'
                className = 'captions-translate-icon'
                height = '20'
                viewBox = '0 0 24 24'
                width = '20'>
                <path
                    d = 'M3.6 12.6 1.8 16.4l4.4-1.8Z'
                    fill = '#2196f3' />
                <circle
                    cx = '8.5'
                    cy = '8.5'
                    fill = '#2196f3'
                    r = '6.5' />
                <path
                    d = 'M5.3 10.8 7.75 4.8l2.45 6M6.2 8.8h3.1'
                    fill = 'none'
                    stroke = '#fff'
                    strokeLinecap = 'round'
                    strokeLinejoin = 'round'
                    strokeWidth = '1.4' />
                <path
                    d = 'm19.6 19.4 3 3-5-1.2Z'
                    fill = '#ffb300' />
                <circle
                    className = 'captions-translate-icon-front'
                    cx = '15.5'
                    cy = '15'
                    fill = '#ffb300'
                    r = '6.5' />
                <path
                    d = 'M15.8 10.9V12M12.9 12.4h5.8M14 12.4q1.2 4.2 4.6 6.2M17.6 12.4q-1.2 4.2-4.6 6.2'
                    fill = 'none'
                    stroke = '#fff'
                    strokeLinecap = 'round'
                    strokeWidth = '1.3' />
            </svg>
        );
    }

    _getFixedTranscriptItems = () => {
        const { _transcriptMessages, _localParticipant } = this.props;

        if (!_transcriptMessages || !_transcriptMessages.size) {
            if (this.props._transcripts && this.props._transcripts.size) {
                const fallbackItems = [];

                for (const [ id, fullText ] of this.props._transcripts) {
                    const colonIdx = fullText.indexOf(':');
                    const name = colonIdx > -1 ? fullText.substring(0, colonIdx).trim() : 'Unknown';
                    const text = colonIdx > -1 ? fullText.substring(colonIdx + 1).trim() : fullText;

                    const localId = _localParticipant?.id;
                    const localName = _localParticipant?.name?.trim().toLowerCase();
                    const trimmedName = name.trim().toLowerCase();

                    const isLocal = Boolean(
                        (localName && (trimmedName === localName || trimmedName === 'you'))
                        || trimmedName === 'you'
                        || (localId && trimmedName === localId.toLowerCase())
                    );

                    fallbackItems.push({
                        id,
                        participantId: isLocal ? localId : undefined,
                        name,
                        avatarUrl: isLocal ? _localParticipant?.avatarURL : undefined,
                        text,
                        isLocal,
                        isFinal: true
                    });
                }

                return fallbackItems;
            }

            return [];
        }

        const items = [];

        for (const [ id, message ] of _transcriptMessages) {
            if (!message) {
                continue;
            }

            let text = '';

            if (message.final) {
                text = message.final;
            } else {
                const stable = message.stable || '';
                const unstable = message.unstable || '';

                text = stable + unstable;
            }

            text = text ? text.trim() : '';

            if (!text) {
                continue;
            }

            const participant = message.participant || {};
            const localId = _localParticipant?.id;
            const localName = _localParticipant?.name?.trim().toLowerCase();
            const pId = participant.id;
            const pName = participant.name?.trim().toLowerCase();

            const isLocal = Boolean(
                (localId && pId && (pId === localId || pId.includes(localId) || localId.includes(pId)))
                || (localName && pName && (pName === localName || pName === 'you'))
                || pName === 'you'
                || pId === 'local'
            );

            items.push({
                id,
                participantId: participant.id,
                name: participant.name || 'Unknown',
                avatarUrl: participant.avatarUrl,
                text,
                isLocal,
                isFinal: Boolean(message.final)
            });
        }

        return items;
    };

    /**
     * Renders the captions dialog UI, docked ('fixed') or draggable ('float').
     *
     * @returns {React$Element|null}
     */
    _renderFixedCaptions() {
        const {
            _captionsView,
            _language,
            t,
            confSettings,
            transcription,
            isLocalModerator,
            _requestingCaptionsModal,
            _translatedVoiceEnabled,
            _translatedVoiceUnavailableReason,
            tileviewStatus
        } = this.props;

        if (!_requestingCaptionsModal) {
            return null;
        }

        const {
            searchQuery,
            captionFontSize,
            showCurrentCaption,
            isDragging,
            isDragged,
            position
        } = this.state;

        const { language: currentLanguage } = i18next;
        const primaryLang = (currentLanguage || 'en').split(/[-_]/)[0].toLowerCase();
        const currentLangData = LIBRE_SUPPORTED_LANGS.find(lang => lang.code === primaryLang)
            || LIBRE_SUPPORTED_LANGS.find(lang => lang.code === 'en')
            || LIBRE_SUPPORTED_LANGS[0];
        const targetLanguages = currentLangData ? currentLangData.targets : [];
        const currentTargetLang = _language ? _language.split(/[-_]/)[0].toLowerCase() : '';
        const noLanguageLabel = TRANSLATION_LANGUAGES_HEAD;
        const isFixedView = _captionsView === 'fixed';

        const languageClicked = value => {
            const selectedLanguage = value === noLanguageLabel ? null : value;
            const enabled = Boolean(selectedLanguage);
            const displaySubtitles = enabled;

            if (enabled === true) {
                if (transcription?.enabled && Boolean(selectedLanguage) === false) {
                    if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                        if (isLocalModerator) {
                            this.props.dispatch(showErrorNotification({
                                descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                                titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                            }));
                        }
                    } else {
                        this.props.dispatch(selectTranslationLanguage(enabled, displaySubtitles, selectedLanguage));
                    }
                } else {
                    this.props.dispatch(setRequestingSubtitles(enabled, displaySubtitles, selectedLanguage));
                }
            } else {
                this.props.dispatch(selectTranslationLanguage(enabled, displaySubtitles, selectedLanguage));
            }
        };

        const filteredTargetLangs = targetLanguages.filter(lang => {
            if (!searchQuery) {
                return true;
            }
            const translatedName = t(`languages:${lang}`).toLowerCase();
            const q = searchQuery.trim().toLowerCase();

            return translatedName.includes(q) || lang.toLowerCase().includes(q);
        });

        const noneOption = (
            <DropdownItem
                className = { `captions-language-item ${currentTargetLang ? '' : 'selected-option'}` }
                isDisabled = { false }
                isSelected = { !currentTargetLang }
                key = 'none'
                onClick = { e => {
                    e.stopPropagation();
                    this.setState({ searchQuery: '' });
                    languageClicked(noLanguageLabel);
                } }>
                <div className = 'captions-language-item-label'>
                    <span>{ t('transcribing.noTranslation') }</span>
                    { !currentTargetLang && (
                        <svg
                            className = 'captions-selected-check'
                            height = '16'
                            viewBox = '0 0 24 24'
                            width = '16'>
                            <path
                                d = 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'
                                fill = 'currentColor' />
                        </svg>
                    ) }
                </div>
            </DropdownItem>
        );

        const translationLanguageItems = filteredTargetLangs.length > 0
            ? filteredTargetLangs.map(lang => {
                const isSelected = lang === currentTargetLang;

                return (
                    <DropdownItem
                        className = { `captions-language-item ${isSelected ? 'selected-option' : ''}` }
                        isDisabled = { false }
                        isSelected = { isSelected }
                        key = { lang }
                        onClick = { e => {
                            e.stopPropagation();
                            this.setState({ searchQuery: '' });
                            languageClicked(lang);
                        } }>
                        <div className = 'captions-language-item-label'>
                            <span>{ t(`languages:${lang}`) }</span>
                            { isSelected && (
                                <svg
                                    className = 'captions-selected-check'
                                    height = '16'
                                    viewBox = '0 0 24 24'
                                    width = '16'>
                                    <path
                                        d = 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'
                                        fill = 'currentColor' />
                                </svg>
                            ) }
                        </div>
                    </DropdownItem>
                );
            })
            : (
                <div className = 'captions-no-languages-found'>
                    <span>{ t('transcribing.noLanguagesFound') }</span>
                </div>
            );

        const items = this._getFixedTranscriptItems();
        const targetLangDisplay = currentTargetLang
            ? t(`languages:${currentTargetLang}`)
            : t('transcribing.TranslateTo');
        const voiceAvailable = !_translatedVoiceUnavailableReason;
        const voiceOn = voiceAvailable && Boolean(_translatedVoiceEnabled);
        const voiceTitle = voiceAvailable
            ? `${t(voiceOn ? 'transcribing.translatedVoiceOn' : 'transcribing.translatedVoiceOff')}. ${
                t('transcribing.translatedVoiceDesc')}`
            : t(`transcribing.translatedVoiceUnavailable.${_translatedVoiceUnavailableReason}`);
        const viewToggleLabel = t(isFixedView ? 'transcribing.floatCaptions' : 'transcribing.dockCaptions');

        const containerStyle = isFixedView
            ? {}
            : {
                transform: isDragged
                    ? `translate(${position.x}px, ${position.y}px)`
                    : tileviewStatus
                        ? 'translate(-50%, 30px)'
                        : 'translate(-50%, 20px)',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
            };

        const containerClassName = [
            'captions-fixed-container',
            isFixedView ? 'fixed-view' : 'float-view',
            captionFontSize === 'large' ? 'font-large' : 'font-normal',
            isDragging ? 'is-dragging' : ''
        ].filter(Boolean).join(' ');

        return (
            <div
                className = { containerClassName }
                onMouseDown = { this._onHandleMouseDown }
                onTouchStart = { this._onHandleTouchStart }
                ref = { this.containerRef }
                style = { containerStyle }>
                <div className = 'captions-fixed-header'>
                    <div className = 'captions-fixed-header-left'>
                        <DropdownMenu
                            className = 'dropdown-min-width captions-dropdown-menu'
                            placement = { this._getDropdownPlacement() }
                            trigger = {
                                <div
                                    className = { `captions-fixed-language-pill ${currentTargetLang ? 'has-language' : ''}` }
                                    title = { t('transcribing.TranslateTo') }>
                                    { this._renderTranslateIcon() }
                                    <span>{ targetLangDisplay }</span>
                                    <svg
                                        className = 'captions-language-chevron'
                                        height = '12'
                                        viewBox = '0 0 24 24'
                                        width = '12'>
                                        <path
                                            d = 'M7 10l5 5 5-5'
                                            fill = 'none'
                                            stroke = 'currentColor'
                                            strokeLinecap = 'round'
                                            strokeLinejoin = 'round'
                                            strokeWidth = '2' />
                                    </svg>
                                </div>
                            }
                            triggerButtonProps = {{
                                shouldFitContainer: false,
                                className: 'captions-fixed-language-trigger-wrap'
                            }}
                            triggerType = 'button'>
                            <DropdownItemGroup>
                                <div
                                    className = 'captions-search-container'
                                    onClick = { e => e.stopPropagation() }>
                                    <svg
                                        className = 'captions-search-icon'
                                        height = '14'
                                        viewBox = '0 0 24 24'
                                        width = '14'>
                                        <path
                                            // eslint-disable-next-line max-len
                                            d = 'M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'
                                            fill = 'currentColor' />
                                    </svg>
                                    <input
                                        autoFocus = { true }
                                        className = 'captions-search-input'
                                        onChange = { this._onSearchChange }
                                        onClick = { e => e.stopPropagation() }
                                        placeholder = { t('transcribing.searchLanguage') }
                                        type = 'text'
                                        value = { searchQuery } />
                                    { searchQuery ? (
                                        <button
                                            aria-label = { t('transcribing.clearSearch') }
                                            className = 'captions-search-clear'
                                            onClick = { e => {
                                                e.stopPropagation();
                                                this.setState({ searchQuery: '' });
                                            } }
                                            type = 'button'>
                                            ✕
                                        </button>
                                    ) : null }
                                </div>
                                <div className = 'captions-dropdown-items-scroll'>
                                    { noneOption }
                                    { translationLanguageItems }
                                </div>
                            </DropdownItemGroup>
                        </DropdownMenu>
                    </div>

                    <div
                        className = 'captions-fixed-drag-handle'
                        title = { t('transcribing.dragToMove') }>
                        <span className = 'captions-drag-grip' />
                    </div>

                    <div className = 'captions-fixed-header-right'>
                        <button
                            aria-disabled = { !voiceAvailable }
                            aria-label = { t('transcribing.translatedVoice') }
                            aria-pressed = { voiceOn }
                            className = { `captions-voice-toggle ${voiceOn ? 'on' : ''} ${voiceAvailable ? '' : 'unavailable'}` }
                            onClick = { this._onToggleTranslatedVoice }
                            title = { voiceTitle }
                            type = 'button'>
                            <svg
                                aria-hidden = 'true'
                                fill = 'none'
                                height = '15'
                                stroke = 'currentColor'
                                strokeLinecap = 'round'
                                strokeLinejoin = 'round'
                                strokeWidth = '2'
                                viewBox = '0 0 24 24'
                                width = '15'>
                                <path d = 'M11 5 6 9H2v6h4l5 4V5z' />
                                { voiceOn
                                    ? <path d = 'M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13' />
                                    : <path d = 'm16 9 6 6M22 9l-6 6' /> }
                            </svg>
                            <span className = 'captions-voice-toggle-label'>{ t('transcribing.translatedVoice') }</span>
                            <span className = 'captions-voice-switch'>
                                <span className = 'captions-voice-switch-knob' />
                            </span>
                        </button>

                        <span className = 'captions-header-divider' />

                        <button
                            aria-label = { t('transcribing.captionSize') }
                            className = { `captions-fixed-icon-btn ${captionFontSize === 'large' ? 'active' : ''}` }
                            onClick = { e => {
                                e.stopPropagation();
                                this._onToggleFontSize();
                            } }
                            title = { t('transcribing.captionSize') }
                            type = 'button'>
                            <span className = 'captions-size-glyph'>
                                <span className = 'big'>A</span>
                                <span className = 'small'>A</span>
                            </span>
                        </button>

                        <button
                            aria-label = { viewToggleLabel }
                            className = 'captions-fixed-icon-btn'
                            onClick = { this._onToggleCaptionsView }
                            title = { viewToggleLabel }
                            type = 'button'>
                            <Icon
                                size = { 16 }
                                src = { isFixedView ? FloatingIcon : FixedIcon } />
                        </button>

                        <button
                            aria-label = { t('transcribing.captionsSettings') }
                            className = 'captions-fixed-icon-btn'
                            onClick = { () => this.props.dispatch(toggleLanguageSelectorDialog()) }
                            title = { t('transcribing.captionsSettings') }
                            type = 'button'>
                            <Icon
                                size = { 16 }
                                src = { IconSettings } />
                        </button>

                        <button
                            aria-label = { t('transcribing.closeCaptions') }
                            className = 'captions-fixed-icon-btn close-btn'
                            onClick = { this._onHandleCloseCaptionsModal }
                            title = { t('transcribing.closeCaptions') }
                            type = 'button'>
                            <svg
                                fill = 'currentColor'
                                height = '14'
                                viewBox = '0 0 24 24'
                                width = '14'>
                                <path d = 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                            </svg>
                        </button>
                    </div>
                </div>

                <div
                    className = 'captions-fixed-content'
                    onScroll = { this._onFixedScroll }
                    ref = { this.fixedScrollRef }>
                    { items.length > 0
                        ? items.map((item, index) => (
                            <div
                                className = { `captions-fixed-item ${item.isLocal ? 'local-transcription' : 'remote-transcription'}${
                                    _isSameSpeaker(items[index - 1], item) ? ' same-speaker' : ''}` }
                                key = { item.id }>
                                <div className = 'captions-fixed-body'>
                                    <div className = 'captions-fixed-speaker-name'>
                                        { item.isLocal ? t('chat.you') : item.name }
                                    </div>
                                    <div className = 'captions-fixed-text'>
                                        { item.text }
                                    </div>
                                </div>
                            </div>
                        ))
                        : (
                            <div className = 'captions-fixed-empty'>
                                <span className = 'captions-listening-dots'>
                                    <span />
                                    <span />
                                    <span />
                                </span>
                                { t('transcribing.captionsListening') }
                            </div>
                        ) }
                </div>

                { !showCurrentCaption && (
                    <button
                        className = 'captions-jump-to-bottom-btn'
                        onClick = { this._onJumpToBottom }
                        type = 'button'>
                        <svg
                            fill = 'none'
                            height = '13'
                            stroke = 'currentColor'
                            strokeLinecap = 'round'
                            strokeLinejoin = 'round'
                            strokeWidth = '2.5'
                            viewBox = '0 0 24 24'
                            width = '13'>
                            <line
                                x1 = '12'
                                x2 = '12'
                                y1 = '5'
                                y2 = '19' />
                            <polyline points = '19 12 12 19 5 12' />
                        </svg>
                        <span>{ t('transcribing.jumpToBottom') }</span>
                    </button>
                ) }
            </div>
        );
    }

    /**
     * Subtitles container implementation for AbstractCaptions compatibility.
     *
     * @returns {React$Element|null}
     */
    _renderSubtitlesContainer(): React$Element<*> {
        return this._renderFixedCaptions();
    }

    /**
     * Overrides React's {@link Component#render()} to present the captions dialog
     * in either captions view.
     *
     * @inheritdoc
     * @returns {React$Element|null}
     */
    render() {
        const { _requestingSubtitles, _requestingCaptionsModal } = this.props;

        if (!_requestingSubtitles || !_requestingCaptionsModal) {
            return null;
        }

        return this._renderFixedCaptions();
    }
}

/**
 * Whether two consecutive caption lines come from the same speaker, so the second can drop the repeated name.
 *
 * @param {Object} prev - The previous line, if any.
 * @param {Object} item - The current line.
 * @returns {boolean}
 */
function _isSameSpeaker(prev, item) {
    return Boolean(prev) && prev.isLocal === item.isLocal && prev.name === item.name;
}

/**
 * Adds the translated-voice toggle's own state on top of the props shared with the native captions component.
 * Kept here rather than in the abstract component because native has no translated voice.
 *
 * @param {Object} state - The redux state.
 * @returns {Object}
 */
function _mapStateToProps(state) {
    return {
        ..._abstractMapStateToProps(state),
        _captionsView: state['features/subtitles']._captionsView,
        _translatedVoiceEnabled: state['features/subtitles']._translatedVoiceEnabled,
        _translatedVoiceUnavailableReason: getTranslatedVoiceUnavailableReason(state)
    };
}

export default translate(connect(_mapStateToProps)(Captions));
